import {
  resourcePermissionsOperations,
  type PermissionContext,
} from '@ardoq/access-control';
import type { APIFieldAttributes } from '@ardoq/api-types';
import {
  featureOperations,
  Features,
  type FeatureContext,
} from '@ardoq/features';
import { commonIntegrationApi } from '@ardoq/api';
import type {
  ResponseDataCell,
  SelectionResponseTable,
  TabularSampleResponse,
} from '@ardoq/api-types/integrations';
import { isArdoqError } from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import { integrationViewState$ } from 'integrations/integrationViewState$';
import { getActiveIntegrationStream } from 'integrations/common/streams/activeIntegrations/activeIntegrations$';
import { isIntegrationId } from 'integrations/common/streams/activeIntegrations/utils';
import { getTabularMappingConstraintsStream } from 'integrations/common/streams/tabularMappingConstraints/getTabularMappingConstraintsStream';
import type {
  TableMappingConstraints,
  TabularMappingConstraints,
} from 'integrations/common/streams/tabularMappingConstraints/types';
import { fields$ } from 'integrations/common/streams/fields/fields$';
import { selectionState$ } from 'integrations/common/streams/selectionState/selectionState$';
import { tabularMappings$ } from 'integrations/common/streams/tabularMappings/getTabularMappingStream';
import type {
  IntegrationId,
  TabularMapping,
} from 'integrations/common/streams/tabularMappings/types';
import { transferState$ } from 'integrations/common/streams/transferState/getTransferStateStream';
import { isResourceCreationDisabled } from 'integrations/common/streams/transferState/utils';
import { isFieldMappingAllowed } from 'integrations/common/streams/tabularMappings/utils';
import { existingWorkspaces$ } from 'integrations/common/streams/workspaces/workspaces$';
import { featureContext$ } from 'streams/featureContext$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import {
  referenceTypesByWorkspaceId$,
  type ReferenceTypesByWorkspaceId,
} from 'streams/models/referenceTypesByWorkspaceId$';
import {
  componentTypesByWorkspaceId$,
  type ComponentTypesByWorkspaceId,
} from 'streams/models/componentTypesByWorkspaceId$';
import {
  combineLatest,
  distinctUntilChanged,
  from,
  map,
  of,
  switchMap,
  type Observable,
} from 'rxjs';
import { buildAutomapColumnsCommand } from './command';
import type { AutomapColumnsContext, LockedColumn } from './types';

const MAX_SAMPLE_CELL_LENGTH = 100;

type SampleRowsByTableId = Record<string, ResponseDataCell[][]>;

const truncateSampleCell = (cell: ResponseDataCell): ResponseDataCell =>
  typeof cell === 'string' && cell.length > MAX_SAMPLE_CELL_LENGTH
    ? `${cell.slice(0, MAX_SAMPLE_CELL_LENGTH)}…`
    : cell;

const toSampleRowsByTableId = ({
  tables,
}: TabularSampleResponse): SampleRowsByTableId =>
  Object.fromEntries(
    tables.map(({ id, rows }) => [
      id,
      rows.map(row => row.map(truncateSampleCell)),
    ])
  );

const sampleRowsBySourceId = new Map<string, SampleRowsByTableId>();

const fetchSampleRowsByTableId = async (
  sourceId: string
): Promise<SampleRowsByTableId> => {
  const cachedSampleRows = sampleRowsBySourceId.get(sourceId);
  if (cachedSampleRows) return cachedSampleRows;

  const sampleResult = await commonIntegrationApi.fetchTabularSample(sourceId);
  if (isArdoqError(sampleResult)) {
    logError(sampleResult, 'Failed to fetch tabular sample', {
      tags: { team: 'integrations' },
    });
    return {};
  }
  const sampleRows = toSampleRowsByTableId(sampleResult);
  sampleRowsBySourceId.clear();
  sampleRowsBySourceId.set(sourceId, sampleRows);
  return sampleRows;
};

const toSampleRowsByTableId$ = (
  integrationId: IntegrationId
): Observable<SampleRowsByTableId> =>
  getActiveIntegrationStream(integrationId).pipe(
    map(({ sourceId }) => sourceId),
    distinctUntilChanged(),
    switchMap(sourceId =>
      sourceId ? from(fetchSampleRowsByTableId(sourceId)) : of({})
    )
  );

const toLockedColumns = (
  sourceFieldNames: string[],
  { mappedColumns }: TableMappingConstraints
): LockedColumn[] =>
  sourceFieldNames.flatMap((sourceFieldName, columnIndex) => {
    const constraint = mappedColumns[sourceFieldName];
    return constraint?.disabledColumnType
      ? [{ columnIndex, columnType: constraint.columnType }]
      : [];
  });

const toContextTable = (
  table: SelectionResponseTable,
  tabularMapping: TabularMapping,
  tabularMappingConstraints: TabularMappingConstraints,
  sampleRowsByTableId: SampleRowsByTableId
): AutomapColumnsContext['tables'][number] => {
  const sourceFieldNames =
    table.sourceFieldNames ?? table.columns.map(({ id }) => id);
  const constraints = tabularMappingConstraints[table.id];
  return {
    id: table.id,
    name: table.name,
    index: table.index,
    columns: table.columns.map(({ header }) => header),
    sourceFieldNames,
    sampleRows: sampleRowsByTableId[table.id] ?? [],
    rowRepresentation: tabularMapping[table.id]?.rowRepresentation ?? null,
    lockedRowRepresentation: constraints?.disabledReferenceType
      ? constraints.tableType
      : null,
    lockedColumns: constraints
      ? toLockedColumns(sourceFieldNames, constraints)
      : [],
  };
};

const toContextWorkspace = (
  { _id, name }: { _id: string; name: string },
  referenceTypesByWorkspaceId: ReferenceTypesByWorkspaceId,
  componentTypesByWorkspaceId: ComponentTypesByWorkspaceId
): AutomapColumnsContext['workspaces'][number] => ({
  id: _id,
  name,
  componentTypes: (componentTypesByWorkspaceId[_id] ?? []).map(
    componentType => componentType.name
  ),
  referenceTypes: (referenceTypesByWorkspaceId[_id] ?? []).map(
    referenceType => referenceType.name
  ),
});

const toAvailableCommands$ = (integrationId: IntegrationId) =>
  combineLatest([
    selectionState$,
    tabularMappings$,
    existingWorkspaces$,
    referenceTypesByWorkspaceId$,
    transferState$,
    currentUserPermissionContext$,
    componentTypesByWorkspaceId$,
    fields$,
    getTabularMappingConstraintsStream(integrationId),
    toSampleRowsByTableId$(integrationId),
  ]).pipe(
    map(
      ([
        selectionState,
        tabularMappings,
        existingWorkspaces,
        referenceTypesByWorkspaceId,
        transferState,
        permissionContext,
        componentTypesByWorkspaceId,
        { all: allFields },
        tabularMappingConstraints,
        sampleRowsByTableId,
      ]) => {
        const selection = selectionState[integrationId];
        if (selection?.status !== 'SUCCESS') return [];
        const tables = selection.data.tables.map(table =>
          toContextTable(
            table,
            tabularMappings[integrationId],
            tabularMappingConstraints ?? {},
            sampleRowsByTableId
          )
        );
        if (tables.length === 0) return [];
        const canCreateTypes = !isResourceCreationDisabled(
          transferState[integrationId]
        );
        const editableWorkspaces = existingWorkspaces.filter(
          ({ hasEditAccess }) => hasEditAccess
        );
        const editableWorkspaceIds = editableWorkspaces.map(({ _id }) => _id);
        return [
          buildAutomapColumnsCommand({
            integrationId,
            tables,
            workspaces: editableWorkspaces.map(workspace =>
              toContextWorkspace(
                workspace,
                referenceTypesByWorkspaceId,
                componentTypesByWorkspaceId
              )
            ),
            canCreateWorkspace:
              canCreateTypes &&
              resourcePermissionsOperations.canCreateWorkspace(
                permissionContext
              ),
            canCreateTypes,
            fields: allFields
              .filter(field =>
                isFieldMappableInAll(
                  field,
                  permissionContext,
                  editableWorkspaceIds
                )
              )
              .map(({ name, label, type }) => ({
                name,
                label,
                type,
              })),
          }),
        ];
      }
    )
  );

const isFieldMappableInAll = (
  field: APIFieldAttributes,
  permissionContext: PermissionContext,
  workspaceIds: string[]
): boolean =>
  workspaceIds.every(workspaceId =>
    isFieldMappingAllowed(field, permissionContext, workspaceId)
  );

const toSelectedIntegrationId = ({
  selectedIntegrationId,
}: {
  selectedIntegrationId: string | null;
}): IntegrationId | null =>
  isIntegrationId(selectedIntegrationId) ? selectedIntegrationId : null;

const hasAutomapFeature = (featureContext: FeatureContext): boolean =>
  featureOperations.hasFeature(
    featureContext,
    Features.INTEGRATIONS_TMP_AI_AUTOMAP_COLUMNS
  );

export const availableCommands$ = combineLatest([
  integrationViewState$.pipe(
    map(toSelectedIntegrationId),
    distinctUntilChanged()
  ),
  featureContext$.pipe(map(hasAutomapFeature), distinctUntilChanged()),
]).pipe(
  switchMap(([integrationId, isAutomapEnabled]) =>
    integrationId && isAutomapEnabled
      ? toAvailableCommands$(integrationId)
      : of([])
  )
);
